import { useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import Brand from "../components/Brand";
import { api } from "../lib/api";
import { Heart, Sparkles, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [5, 10, 25, 50, 100, 250];

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get("canceled") === "1";
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 1) {
      toast.error("Please choose a gift amount of at least $1.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/donate/checkout", {
        amount_usd: finalAmount,
        name, email, message,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;  // Off to Stripe
      } else {
        toast.error("Could not start checkout. Please try again.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteNav/>

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1A2E1F 100%)" }}>
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full opacity-25 pointer-events-none"
             style={{ background: "radial-gradient(circle, #F39A1F 0%, transparent 70%)" }}/>
        <div className="relative max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16 text-center">
          <Heart className="w-10 h-10 mx-auto" style={{ color: "#F39A1F" }}/>
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mt-3" style={{ color: "#F39A1F" }}>
            ★ DONATE TO <Brand light suffix=""/> ★
          </div>
          <h1 className="text-4xl sm:text-5xl uppercase mt-3 leading-[1.05] text-white" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, textWrap: "balance" }}>
            Give What You Feel Led To Give.
          </h1>
          <p className="text-white/85 mt-3 max-w-2xl mx-auto" style={{ textWrap: "balance" }}>
            <Brand light suffix=""/> is built so people without big budgets can still learn, share, and earn. If our tools and teaching have blessed you, a freewill offering helps Mike keep the lights on, the servers running, and the ministry growing.
          </p>
          <p className="text-white/65 italic mt-2 text-sm">"Each one must give as he has decided in his heart, not reluctantly or under compulsion, for God loves a cheerful giver." — 2 Corinthians 9:7</p>
        </div>
      </section>

      {/* Cancel notice */}
      {canceled && (
        <div className="max-w-3xl mx-auto px-6 md:px-8 mt-6">
          <div className="rounded-xl bg-white border-2 border-arama px-5 py-4 text-sm text-foreground">
            No problem — your gift wasn't completed. You can try again whenever you'd like.
          </div>
        </div>
      )}

      {/* FORM */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <form onSubmit={submit} data-testid="donate-form" className="rounded-2xl bg-white shadow-2xl border-2 border-arama p-6 sm:p-8">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-gold-deep mb-3 text-center">★ Choose a gift amount ★</div>

          {/* Preset amounts */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p} type="button"
                onClick={() => { setAmount(p); setCustom(""); }}
                data-testid={`donate-preset-${p}`}
                className={`py-3 rounded-md font-bold text-base transition border-2
                  ${amount === p && !custom
                    ? "bg-faith text-white border-faith shadow-lg scale-[1.03]"
                    : "bg-white text-foreground border-border hover:border-faith"}`}
              >
                ${p}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-wider-cap font-bold text-foreground/70">Or enter a custom amount</label>
            <div className="mt-1 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/60 text-lg font-bold">$</span>
              <input
                type="number" min="1" step="1" inputMode="numeric"
                value={custom}
                onChange={(e) => { setCustom(e.target.value); }}
                onFocus={() => setAmount(0)}
                data-testid="donate-custom-amount"
                placeholder="Other"
                className="w-full pl-9 pr-4 py-3 rounded-md border-2 border-border focus:border-arama outline-none transition text-lg font-bold"
              />
            </div>
          </div>

          {/* Optional donor info */}
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <div>
              <label className="text-[11px] uppercase tracking-wider-cap font-bold text-foreground/70">Your Name <span className="text-foreground/40 normal-case font-normal">(optional)</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="donate-name"
                     placeholder="So Mike can thank you"
                     className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border focus:border-arama outline-none transition"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider-cap font-bold text-foreground/70">Email <span className="text-foreground/40 normal-case font-normal">(for receipt)</span></label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="donate-email"
                     placeholder="your@email.com"
                     className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border focus:border-arama outline-none transition"/>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wider-cap font-bold text-foreground/70">A short note for Mike <span className="text-foreground/40 normal-case font-normal">(optional)</span></label>
            <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="donate-message"
                      placeholder="Prayer request, encouragement, or a verse..."
                      className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border focus:border-arama outline-none transition resize-y"/>
          </div>

          {/* Summary + CTA */}
          <div className="mt-6 rounded-xl bg-mint p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-wider-cap font-bold text-foreground/70">Your gift</div>
              <div className="text-3xl font-bold text-red-wine" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                ${finalAmount && finalAmount > 0 ? Number(finalAmount).toFixed(2) : "—"}
              </div>
            </div>
            <button type="submit" disabled={busy || !finalAmount || finalAmount < 1} data-testid="donate-submit"
                    className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Redirecting…</> : <><Heart className="w-4 h-4"/> Give Now <ArrowRight className="w-4 h-4"/></>}
            </button>
          </div>

          <p className="text-[11px] text-center italic text-foreground/60 mt-4">
            Secure one-time gift processed by Stripe · You'll receive an email receipt · No subscription, no surprises.
          </p>
        </form>

        {/* Reassurance */}
        <div className="mt-7 grid sm:grid-cols-3 gap-3 text-center">
          {[
            { Icon: CheckCircle2, label: "Secure", desc: "256-bit Stripe checkout" },
            { Icon: Heart,        label: "Cheerful Givers", desc: "Give only what you feel led" },
            { Icon: Sparkles,     label: "Goes Straight To Mike", desc: "Hosting, content, and helping members" },
          ].map(({ Icon, label, desc }) => (
            <div key={label} className="rounded-xl bg-white border border-border p-4">
              <Icon className="w-6 h-6 mx-auto text-faith"/>
              <div className="font-bold text-foreground mt-1">{label}</div>
              <div className="text-xs text-foreground/70">{desc}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* /donate/thank-you — confirmation page after Stripe returns */
export function DonateThankYou() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  return (
    <div className="min-h-screen bg-white">
      <SiteNav/>
      <section className="max-w-2xl mx-auto px-6 md:px-8 py-20 text-center">
        <Heart className="w-12 h-12 mx-auto text-faith"/>
        <h1 className="text-4xl sm:text-5xl uppercase mt-4 leading-[1.05] text-red-wine" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>
          Thank you.
        </h1>
        <p className="text-foreground mt-3 max-w-md mx-auto text-lg" style={{ textWrap: "balance" }}>
          Your gift helps Mike keep <Brand suffix=".com"/> open, learning, and growing. May the Lord bless you abundantly.
        </p>
        <p className="text-sm italic text-foreground/70 mt-3">"Give, and it will be given to you. A good measure, pressed down, shaken together and running over, will be poured into your lap." — Luke 6:38</p>
        {sid && <div className="mt-5 text-[10px] tracking-wider-cap text-foreground/40 font-mono break-all">Receipt ID: {sid}</div>}
        <a href="/" className="btn-primary inline-flex items-center gap-2 mt-7"><ArrowRight className="w-4 h-4"/> Back Home</a>
      </section>
    </div>
  );
}
